'use client';

import { ThemeProvider } from '@/components/theme-provider';
import Navigation from '@/components/Navigation';
import { Toaster as SonnerToaster } from 'sonner';
import { Toaster as ShadcnToaster } from '@/components/ui/toaster';
import { Providers } from '@/app/providers';
import ScrollToTop from '@/components/ScrollToTop';
import Footer from '@/components/Footer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

interface ClientLayoutProps {
  children: React.ReactNode;
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <h2 className="text-2xl font-bold mb-4">Coś poszło nie tak</h2>
      <p className="text-gray-600 mb-4">{error.message}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
      >
        Spróbuj ponownie
      </button>
    </div>
  );
}

function LoadingFallback() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      <p className="mt-6 text-xl text-center">Trwa inicjalizacja aplikacji...</p>
      <p className="mt-2 text-sm text-gray-600 text-center">Pierwsze uruchomienie może potrwać do 30 sekund. <br />Prosimy o cierpliwość.</p>
    </div>
  );
}

export default function ClientLayout({ children }: ClientLayoutProps) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 30000,
        gcTime: 300000
      },
    },
  }));

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<LoadingFallback />}>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem
            disableTransitionOnChange
          >
            <Providers>
              <Navigation />
              <main className="container mx-auto px-4 py-8 mt-[146px] flex-1">
                {children}
              </main>
              <Footer />
              <ScrollToTop />
              <SonnerToaster richColors position="bottom-center" />
              <ShadcnToaster />
            </Providers>
          </ThemeProvider>
        </Suspense>
      </QueryClientProvider>
    </ErrorBoundary>
  );
} 